import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { FormValues } from '@frontend/design-system';
import { DEFAULT_TASK_TIME, TaskDateTimeFormat, calendarDateFormat } from '../constants';
import { CreateTaskFormType } from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getFulfilledValues = (values: FormValues<CreateTaskFormType>) => {
  const updatedValues: FormValues<CreateTaskFormType> = {};
  // Iterate through all form values and only include non-empty values
  Object.entries(values).forEach(([key, value]) => {
    // Check if value exists and is not empty
    if (
      value !== undefined &&
      value !== null &&
      // For strings, check if they're non-empty
      !(typeof value === 'string' && value === '') &&
      // For arrays, check if they're non-empty
      !(Array.isArray(value) && value.length === 0)
    ) {
      updatedValues[key as keyof CreateTaskFormType] = value;
    }
  });

  return updatedValues;
};

export const hasSameValues = (obj1: FormValues<CreateTaskFormType>, obj2: TaskCenterTypes.Task): boolean => {
  const keyMapping: Record<keyof FormValues<CreateTaskFormType>, keyof TaskCenterTypes.Task> = {
    title: 'title',
    description: 'description',
    locationId: 'locationId',
    patient: 'patient',
    dueDate: 'dueDate',
    assignee: 'assignee',
    status: 'status',
    priority: 'priority',
  };

  return Object.entries(obj1).every(([key, value]) => {
    const mappedKey = keyMapping[key as keyof FormValues<CreateTaskFormType>];
    if (key === 'patient') {
      return obj2.patient?.id && value ? obj2.patient?.id == value : true;
      // Checking if both the values are not present in the form and the task, and the type of the values mismatches
    } else if (typeof obj2[mappedKey] !== 'string' && value === '') return true;
    return obj2[mappedKey] === value;
  });
};

export const formatTaskDate = (date?: string) => {
  if (!date) return '';
  return dayjs(date).format('MMMM D, YYYY');
};

export const convertDateToUTC = ({
  date,
  timezone,
  hasTime,
}: {
  date?: string;
  timezone?: string;
  hasTime?: boolean;
}) => {
  if (!date || !timezone || date === 'T') return '';

  const [oldFormatDate, time] = date.split('T');

  /*
    When parsing the date with dayjs, the format was MM-DD-YYYY which is acceptable to our weave ecosystem, but in the
    case of safari parsing date with the above format throws an error because the date class is not able to format
    understand it and returns an Invalid Date as value. Hence before sending the date to the BE I'm updating the
    format of the date in the BE payload.
  */

  const newDateFormat = [dayjs(oldFormatDate.replaceAll(/-/g, '/')).format(calendarDateFormat), time].join('T');

  if (!hasTime) {
    // Setting a default time for a task before sending it to BE
    return dayjs(newDateFormat.replace('T', ''))
      .hour(DEFAULT_TASK_TIME)
      .tz(timezone, true)
      .utc()
      .format(TaskDateTimeFormat);
  }
  return dayjs(newDateFormat).tz(timezone, true).utc().format(TaskDateTimeFormat);
};

export const getMinDate = (timezone: string) => {
  if (!timezone) return dayjs().format(calendarDateFormat);
  return dayjs().tz(timezone).format(calendarDateFormat);
};

export const getMinTime = (date: string, timezone: string) => {
  if (!timezone) {
    return '';
  }
  const today = dayjs().tz(timezone).format(calendarDateFormat);
  const inputDate = dayjs(date).format(calendarDateFormat);

  if (inputDate === today) {
    const now = dayjs().tz(timezone);
    const minutes = now.minute();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;
    const minTime = now.minute(roundedMinutes).second(0).format('HH:mm:ss');
    return minTime;
  }

  return '';
};
